import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { showSuccess, showError } from '@/lib/toast';
import {
  Webhook, Plus, Trash2, ExternalLink, CheckCircle2, XCircle,
  AlertTriangle, RotateCcw, Clock, Activity,
} from 'lucide-react';

interface WebhookItem {
  id: string;
  url: string;
  description: string;
  events: string[];
  active: boolean;
  lastTriggeredAt: string | null;
  failureCount: number;
  createdAt: string;
}

interface Delivery {
  id: string;
  webhookId: string;
  statusCode: number;
  responseTimeMs: number;
  success: boolean;
  timestamp: string;
  event: string;
}

const WEBHOOK_EVENTS = [
  { key: 'instance.created', labelKey: 'webhooks.eventTypes.instanceCreated' },
  { key: 'instance.started', labelKey: 'webhooks.eventTypes.instanceStarted' },
  { key: 'instance.stopped', labelKey: 'webhooks.eventTypes.instanceStopped' },
  { key: 'instance.destroyed', labelKey: 'webhooks.eventTypes.instanceDestroyed' },
  { key: 'backup.completed', labelKey: 'webhooks.eventTypes.backupCompleted' },
  { key: 'channel.connected', labelKey: 'webhooks.eventTypes.channelConnected' },
  { key: 'billing.payment', labelKey: 'webhooks.eventTypes.billingPayment' },
];

export function Webhooks() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [detailWebhook, setDetailWebhook] = useState<WebhookItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WebhookItem | null>(null);

  // Create form state
  const [newUrl, setNewUrl] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newEvents, setNewEvents] = useState<string[]>([]);

  // Queries
  const { data: webhooksData, isLoading } = useQuery({
    queryKey: ['webhooks'],
    queryFn: async () => {
      const { data } = await api.get('/webhooks');
      return data;
    },
  });

  const webhooks: WebhookItem[] = webhooksData?.webhooks || [];

  const { data: deliveriesData } = useQuery({
    queryKey: ['webhook-deliveries', detailWebhook?.id],
    queryFn: async () => {
      const { data } = await api.get(`/webhooks/${detailWebhook!.id}/deliveries`);
      return data;
    },
    enabled: !!detailWebhook,
  });

  const deliveries: Delivery[] = deliveriesData?.deliveries || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/webhooks', {
        url: newUrl,
        description: newDescription,
        events: newEvents,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      showSuccess(t('webhooks.createSuccess'));
      setCreateOpen(false);
      setNewUrl('');
      setNewDescription('');
      setNewEvents([]);
    },
    onError: () => {
      showError(t('webhooks.createError'));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { data } = await api.put(`/webhooks/${id}`, { active });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      showSuccess(t('webhooks.updateSuccess'));
    },
    onError: () => {
      showError(t('webhooks.updateError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/webhooks/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      showSuccess(t('webhooks.deleteSuccess'));
      setDeleteTarget(null);
    },
    onError: () => {
      showError(t('webhooks.deleteError'));
    },
  });

  const retryMutation = useMutation({
    mutationFn: async ({ webhookId, deliveryId }: { webhookId: string; deliveryId: string }) => {
      await api.post(`/webhooks/${webhookId}/deliveries/${deliveryId}/retry`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-deliveries', detailWebhook?.id] });
      showSuccess(t('webhooks.retrySuccess'));
    },
    onError: () => {
      showError(t('webhooks.retryError'));
    },
  });

  const toggleEvent = (eventKey: string) => {
    setNewEvents((prev) =>
      prev.includes(eventKey)
        ? prev.filter((e) => e !== eventKey)
        : [...prev, eventKey],
    );
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return t('webhooks.never');
    return new Date(dateStr).toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Webhook className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Webhooks</h1>
            <p className="text-sm text-muted-foreground">
              {t('webhooks.subtitle')}
            </p>
          </div>
        </div>
        <Button
          className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-4 w-4" />
          {t('webhooks.createWebhook')}
        </Button>
      </div>

      <Separator />

      {/* Loading state */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-36 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : webhooks.length === 0 ? (
        /* Empty state */
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-20">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Webhook className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <h3 className="text-lg font-medium mb-2">
              {t('webhooks.noWebhooks')}
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {t('webhooks.noWebhooksDescription')}
            </p>
          </CardContent>
        </Card>
      ) : (
        /* Webhook list */
        <div className="space-y-4">
          {webhooks.map((wh) => (
            <Card
              key={wh.id}
              className="group transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/5 hover:border-emerald-200 dark:hover:border-emerald-800"
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1 space-y-3">
                    {/* URL and status */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0">
                        <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="font-mono text-sm font-medium truncate">
                          {wh.url}
                        </span>
                      </div>
                      <Badge
                        variant={wh.active ? 'default' : 'secondary'}
                        className={cn(
                          'text-[11px]',
                          wh.active
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400',
                        )}
                      >
                        {wh.active
                          ? t('webhooks.active')
                          : t('webhooks.inactive')}
                      </Badge>
                      {wh.failureCount > 0 && (
                        <Badge variant="destructive" className="text-[11px] gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {wh.failureCount} {t('webhooks.failures')}
                        </Badge>
                      )}
                    </div>

                    {/* Description */}
                    {wh.description && (
                      <p className="text-sm text-muted-foreground">{wh.description}</p>
                    )}

                    {/* Events */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {wh.events.map((event) => (
                        <Badge
                          key={event}
                          variant="outline"
                          className="text-[11px] font-mono"
                        >
                          {event}
                        </Badge>
                      ))}
                    </div>

                    {/* Meta info */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {t('webhooks.lastTriggeredLabel')} {formatDate(wh.lastTriggeredAt)}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs gap-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950 h-8"
                      onClick={() => setDetailWebhook(wh)}
                    >
                      <Activity className="h-3.5 w-3.5" />
                      {t('webhooks.details')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8"
                      onClick={() =>
                        toggleMutation.mutate({ id: wh.id, active: !wh.active })
                      }
                    >
                      {wh.active
                        ? t('webhooks.disable')
                        : t('webhooks.enable')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                      onClick={() => setDeleteTarget(wh)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Webhook Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('webhooks.createWebhook')}</DialogTitle>
            <DialogDescription>
              {t('webhooks.createDescription')}
            </DialogDescription>
          </DialogHeader>

          <Separator />

          <div className="space-y-4">
            {/* URL */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('webhooks.payloadUrl')}
              </label>
              <Input
                placeholder="https://example.com/webhooks"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                className="font-mono text-sm"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('webhooks.description')}
              </label>
              <Input
                placeholder={t('webhooks.descriptionPlaceholder')}
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </div>

            {/* Events */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('webhooks.events')}
              </label>
              <div className="border rounded-lg p-3 space-y-2">
                {WEBHOOK_EVENTS.map((event) => (
                  <label
                    key={event.key}
                    className="flex items-center gap-3 cursor-pointer py-1 px-2 rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={newEvents.includes(event.key)}
                      onChange={() => toggleEvent(event.key)}
                      className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm">
                      {t(event.labelKey)}
                    </span>
                    <span className="text-xs font-mono text-muted-foreground ml-auto">
                      {event.key}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <Button
              className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
              onClick={() => createMutation.mutate()}
              disabled={!newUrl || newEvents.length === 0 || createMutation.isPending}
            >
              <Plus className="h-4 w-4" />
              {createMutation.isPending
                ? t('webhooks.creating')
                : t('webhooks.createWebhook')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Webhook Detail / Delivery History Dialog */}
      <Dialog open={!!detailWebhook} onOpenChange={(open) => !open && setDetailWebhook(null)}>
        <DialogContent className="sm:max-w-2xl">
          {detailWebhook && (
            <>
              <DialogHeader>
                <DialogTitle>{t('webhooks.webhookDetails')}</DialogTitle>
                <DialogDescription className="font-mono text-xs">
                  {detailWebhook.url}
                </DialogDescription>
              </DialogHeader>

              <Separator />

              {/* Info section */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">{t('webhooks.status')}</span>
                  <div className="mt-1">
                    <Badge
                      className={cn(
                        'text-[11px]',
                        detailWebhook.active
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400',
                      )}
                    >
                      {detailWebhook.active ? t('webhooks.active') : t('webhooks.inactive')}
                    </Badge>
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('webhooks.failureCount')}</span>
                  <p className="mt-1 font-medium">{detailWebhook.failureCount}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('webhooks.lastTriggered')}</span>
                  <p className="mt-1">{formatDate(detailWebhook.lastTriggeredAt)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('webhooks.created')}</span>
                  <p className="mt-1">{formatDate(detailWebhook.createdAt)}</p>
                </div>
              </div>

              {/* Events */}
              <div>
                <span className="text-sm text-muted-foreground">{t('webhooks.events')}</span>
                <div className="flex items-center gap-2 flex-wrap mt-1.5">
                  {detailWebhook.events.map((event) => (
                    <Badge key={event} variant="outline" className="text-[11px] font-mono">
                      {event}
                    </Badge>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Delivery History */}
              <div>
                <h4 className="text-sm font-medium mb-3">
                  {t('webhooks.deliveryHistory')}
                </h4>
                {deliveries.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t('webhooks.noDeliveries')}
                  </p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/50 border-b">
                          <th className="text-left p-3 font-medium text-muted-foreground">
                            {t('webhooks.deliveryStatus')}
                          </th>
                          <th className="text-left p-3 font-medium text-muted-foreground">
                            {t('webhooks.deliveryCode')}
                          </th>
                          <th className="text-left p-3 font-medium text-muted-foreground">
                            {t('webhooks.deliveryResponse')}
                          </th>
                          <th className="text-left p-3 font-medium text-muted-foreground">
                            {t('webhooks.deliveryEvent')}
                          </th>
                          <th className="text-left p-3 font-medium text-muted-foreground">
                            {t('webhooks.deliveryTimestamp')}
                          </th>
                          <th className="text-right p-3 font-medium text-muted-foreground">
                            {t('webhooks.deliveryAction')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveries.map((delivery) => (
                          <tr key={delivery.id} className="border-b last:border-b-0 hover:bg-muted/30 transition-colors">
                            <td className="p-3">
                              {delivery.success ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-500" />
                              )}
                            </td>
                            <td className="p-3 font-mono">
                              <Badge
                                variant="outline"
                                className={cn(
                                  'text-[11px]',
                                  delivery.success
                                    ? 'border-emerald-200 text-emerald-700 dark:border-emerald-800 dark:text-emerald-400'
                                    : 'border-red-200 text-red-700 dark:border-red-800 dark:text-red-400',
                                )}
                              >
                                {delivery.statusCode}
                              </Badge>
                            </td>
                            <td className="p-3 text-muted-foreground">
                              {delivery.responseTimeMs}ms
                            </td>
                            <td className="p-3">
                              <span className="font-mono text-xs">{delivery.event}</span>
                            </td>
                            <td className="p-3 text-muted-foreground text-xs">
                              {formatDate(delivery.timestamp)}
                            </td>
                            <td className="p-3 text-right">
                              {!delivery.success && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs gap-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                                  onClick={() =>
                                    retryMutation.mutate({
                                      webhookId: detailWebhook.id,
                                      deliveryId: delivery.id,
                                    })
                                  }
                                  disabled={retryMutation.isPending}
                                >
                                  <RotateCcw className="h-3 w-3" />
                                  {t('webhooks.retry')}
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {t('webhooks.deleteWebhook')}
            </DialogTitle>
            <DialogDescription>
              {t('webhooks.deleteDescription')}
            </DialogDescription>
          </DialogHeader>

          {deleteTarget && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30 p-3">
              <p className="font-mono text-sm truncate">{deleteTarget.url}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              className="gap-2"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              {deleteMutation.isPending
                ? t('webhooks.deleting')
                : t('common.delete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
